import type { PeriodGrain } from '../types/period';
import type {
  IComponentFacts,
  IReportComponentDefinition,
  ReportComponentId,
} from '../types/report';
import { MonthKeyHelper } from './MonthKeyHelper';

const GRAIN_AGGREGATION: Record<PeriodGrain, string> = {
  month: 'Monthly',
  quarter: 'Quarterly',
  ytd: 'Year to date',
  ttm: 'Trailing 12 months',
};

/** Tiles that report a closing balance rather than a flow over the period. */
const BALANCE_TILES: ReadonlySet<ReportComponentId> =
  new Set<ReportComponentId>(['cash_tile', 'runway_tile']);

/** Time-series charts that extend past the books-closed month when open months are on. */
const PRELIMINARY_CHARTS: ReadonlySet<ReportComponentId> =
  new Set<ReportComponentId>([
    'revenue_trend_chart',
    'profit_trend_chart',
    'margin_trend_chart',
    'opex_breakdown_chart',
    'payroll_trend_chart',
    'burn_runway_chart',
  ]);

export interface ISubtitleInput {
  definition: Pick<IReportComponentDefinition, 'id' | 'kind' | 'note'>;
  facts: Pick<IComponentFacts, 'filter' | 'resolved' | 'table' | 'hasOverride'>;
}

/** One-line card subtitle: `<Aggregation>, <period> · <comparison> · <note>`. */
export class SubtitleHelper {
  static readonly SEPARATOR = ' · ';

  static format({ definition, facts }: ISubtitleInput): string {
    const { filter, resolved, hasOverride } = facts;
    const isTile = definition.kind === 'tile';
    const anchor = resolved.months[resolved.months.length - 1];

    const period =
      BALANCE_TILES.has(definition.id) && anchor
        ? `As of ${MonthKeyHelper.label(anchor)}`
        : `${SubtitleHelper.aggregation(filter.grain, Boolean(filter.rangeStart))}, ${resolved.label}`;

    const parts = [period];
    if (!isTile) parts.push(resolved.comparisonLabel);
    if (definition.note) parts.push(definition.note);
    if (SubtitleHelper.showsPreliminary(definition.id, facts)) {
      parts.push('Includes preliminary months');
    }

    const text = parts.join(SubtitleHelper.SEPARATOR);
    return hasOverride ? `Own period${SubtitleHelper.SEPARATOR}${text}` : text;
  }

  static aggregation(grain: PeriodGrain, isRange: boolean): string {
    return isRange ? 'Monthly' : GRAIN_AGGREGATION[grain];
  }

  /** Mirrors `useTrendMonths`: preliminary months only follow a window ending at the boundary. */
  static showsPreliminary(
    id: ReportComponentId,
    facts: Pick<IComponentFacts, 'filter' | 'resolved' | 'table'>
  ): boolean {
    if (
      !PRELIMINARY_CHARTS.has(id) ||
      !(facts.filter.includeOpenMonths ?? false)
    ) {
      return false;
    }
    const { months, preliminaryMonths } = facts.table;
    const lastResolved =
      facts.resolved.months[facts.resolved.months.length - 1];
    return (
      preliminaryMonths.length > 0 && lastResolved === months[months.length - 1]
    );
  }
}
